import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  ViewColumn as ViewColumnIcon,
} from "@mui/icons-material";
import api, { generateUserPDF } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

// Get the user API base URL
const USER_BASE_URL =
  process.env.REACT_APP_USER_API_URL ||
  "https://backend.hunarmandpunjab.org.pk";

interface User {
  _id: string;
  fullName: string;
  email: string;
  rollNumber: string;
  mobile: string;
  courses: string[];
}

interface Challan {
  _id: string;
  userId: User;
  challanId: string;
  amount: number;
  paid: boolean;
  path?: string;
  branchCode?: number;
  txnDate?: string;
  txnId?: number;
  secondEnrollChallan?: boolean;
  createdAt: string;
  updatedAt: string;
}

const AVAILABLE_EXPORT_COLUMNS = [
  { key: "username", label: "username (Full Name)" },
  { key: "duedate", label: "duedate (Custom Due Date)" },
  { key: "challanId", label: "challanId (Challan ID)" },
  { key: "paddedChallanId", label: "paddedChallanId (8-Digit Padded Challan ID)" },
  { key: "psid", label: "psid (Prefix + Challan ID)" },
  { key: "rollNumber", label: "Roll Number" },
  { key: "fullName", label: "Full Name" },
  { key: "fatherName", label: "Father Name" },
  { key: "email", label: "Email" },
  { key: "mobile", label: "Mobile" },
  { key: "cnic", label: "CNIC" },
  { key: "city", label: "City" },
  { key: "course1", label: "Course 1" },
  { key: "course2", label: "Course 2" },
  { key: "amount", label: "Amount" },
  { key: "paid", label: "Payment Status" },
  { key: "txnId", label: "Transaction ID" },
  { key: "txnDate", label: "Transaction Date" },
  { key: "branchCode", label: "Branch Code" },
  { key: "secondEnrollChallan", label: "2nd Enrollment Challan" },
  { key: "createdAt", label: "Created Date" },
  { key: "updatedAt", label: "Updated Date" },
];

interface ChallanResponse {
  challans: Challan[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

interface ChallanStats {
  total: number;
  paid: number;
  unpaid: number;
  totalRevenue: number;
  pendingRevenue: number;
  recent: number;
}

function Challans() {
  const [challans, setChallans] = useState<Challan[]>([]);
  const [stats, setStats] = useState<ChallanStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [paid, setPaid] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [specificDate, setSpecificDate] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [submitDate, setSubmitDate] = useState("");
  const { user } = useAuth();

  // PDF generation states
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedChallanForPDF, setSelectedChallanForPDF] =
    useState<Challan | null>(null);
  const [adminApiKey, setAdminApiKey] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Check if user is admin (hide revenue for admin role)
  const isAdmin = user?.role === "admin";

  // Download Column Selection modal states
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() =>
    AVAILABLE_EXPORT_COLUMNS.map((c) => c.key)
  );
  const [exportDueDate, setExportDueDate] = useState<string>(() => {
    // Default due date: 7 days from today in YYYY-MM-DD
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split("T")[0];
  });
  const [exportPsidPrefix, setExportPsidPrefix] = useState<string>("267200309");

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [debouncedMinAmount, setDebouncedMinAmount] = useState(minAmount);
  const [debouncedMaxAmount, setDebouncedMaxAmount] = useState(maxAmount);

  // Debounce text/number inputs by 400ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMinAmount(minAmount);
    }, 400);
    return () => clearTimeout(handler);
  }, [minAmount]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMaxAmount(maxAmount);
    }, 400);
    return () => clearTimeout(handler);
  }, [maxAmount]);

  useEffect(() => {
    fetchChallans();
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    paid,
    startDate,
    endDate,
    specificDate,
    debouncedMinAmount,
    debouncedMaxAmount,
    submitDate,
  ]);

  useEffect(() => {
    fetchChallanStats();
  }, []);

  const fetchChallans = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (paid !== "") params.paid = paid;
      if (specificDate) {
        params.startDate = specificDate;
        params.endDate = specificDate;
      } else {
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }
      if (debouncedMinAmount) params.minAmount = debouncedMinAmount;
      if (debouncedMaxAmount) params.maxAmount = debouncedMaxAmount;
      if (submitDate) params.submitDate = submitDate;

      const response = await api.get("/challans", { params });
      const data: ChallanResponse = response.data.data;
      setChallans(data.challans);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error("Error fetching challans:", err);
      setError("Failed to load challans");
    } finally {
      setLoading(false);
    }
  };

  const fetchChallanStats = async () => {
    try {
      const response = await api.get("/challans/stats");
      setStats(response.data.data);
    } catch (err) {
      console.error("Error fetching challan stats:", err);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setPaid("");
    setStartDate("");
    setEndDate("");
    setSpecificDate("");
    setMinAmount("");
    setDebouncedMinAmount("");
    setMaxAmount("");
    setDebouncedMaxAmount("");
    setSubmitDate("");
    setPage(0);
  };

  const handleOpenDownloadModal = () => {
    setDownloadModalOpen(true);
  };

  const handleToggleColumn = (colKey: string) => {
    setSelectedColumns((prev) =>
      prev.includes(colKey)
        ? prev.filter((k) => k !== colKey)
        : [...prev, colKey]
    );
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(AVAILABLE_EXPORT_COLUMNS.map((c) => c.key));
  };

  const handleDeselectAllColumns = () => {
    setSelectedColumns([]);
  };

  const downloadChallans = async () => {
    if (selectedColumns.length === 0) {
      alert("Please select at least one column to download.");
      return;
    }

    try {
      setLoading(true);
      setDownloadModalOpen(false);

      // Build the same params as the fetchChallans function
      const params: any = {};

      if (debouncedSearch) params.search = debouncedSearch;
      if (paid !== "") params.paid = paid;
      if (specificDate) {
        params.startDate = specificDate;
        params.endDate = specificDate;
      } else {
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }
      if (debouncedMinAmount) params.minAmount = debouncedMinAmount;
      if (debouncedMaxAmount) params.maxAmount = debouncedMaxAmount;
      if (submitDate) params.submitDate = submitDate;
      params.columns = selectedColumns.join(",");
      if (exportDueDate) params.dueDate = exportDueDate;
      if (exportPsidPrefix) params.psidPrefix = exportPsidPrefix;

      // Call the download endpoint
      const response = await api.get("/challans/download", {
        params,
        responseType: "blob", // Important for file downloads
      });

      // Create download link
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with current date
      const timestamp = new Date().toISOString().split("T")[0];
      const filterInfo = getActiveFilterCount() > 0 ? "_filtered" : "_all";
      link.download = `challans_export${filterInfo}_${timestamp}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading challans:", err);
      setError("Failed to download challans data");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-PK");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getInitials = (name?: string) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // PDF generation handler
  const handleGeneratePDF = (challan: Challan) => {
    if (
      challan.userId &&
      challan.userId._id !== "deleted" &&
      challan.userId._id !== "invalid"
    ) {
      setSelectedChallanForPDF(challan);
      setPdfModalOpen(true);
    } else {
      alert("Cannot generate PDF: User data is not available for this challan");
    }
  };

  const confirmGeneratePDF = async () => {
    if (!adminApiKey.trim()) {
      alert("Please provide the admin API key");
      return;
    }

    if (!selectedChallanForPDF || !selectedChallanForPDF.userId) {
      alert("No valid user selected");
      return;
    }

    try {
      setPdfGenerating(true);

      const response = await generateUserPDF(
        selectedChallanForPDF.userId._id,
        adminApiKey,
        {
          fullName: selectedChallanForPDF.userId.fullName,
          rollNumber: selectedChallanForPDF.userId.rollNumber,
          email: selectedChallanForPDF.userId.email,
        }
      );
      const data = response.data;

      // Both existing and new challans now return JSON with fileUrl
      if (data.status === "success" && data.data && data.data.fileUrl) {
        // Extract path from fileUrl starting at /uploads
        const fileUrl = data.data.fileUrl;
        const uploadPath = fileUrl.substring(fileUrl.indexOf("/uploads"));
        window.open(`${USER_BASE_URL}${uploadPath}`, "_blank");

        // Show appropriate success message
        if (data.message === "Existing challan found") {
          alert(`Existing challan opened: ${data.data.challanNumber}`);
        } else if (data.message === "Challan generated successfully") {
          alert(`New challan generated and opened: ${data.data.challanNumber}`);
        } else {
          alert(`Challan opened: ${data.data.challanNumber}`);
        }
      } else {
        throw new Error("Invalid response format from challan API");
      }

      setPdfModalOpen(false);
      setAdminApiKey("");
    } catch (error: any) {
      console.error("Error generating challan:", error);
      alert(
        `Failed to generate challan: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setPdfGenerating(false);
    }
  };

  const getFilterSummary = () => {
    const hasFilters =
      search ||
      paid !== "" ||
      startDate ||
      endDate ||
      specificDate ||
      minAmount ||
      maxAmount ||
      submitDate;
    const totalDbChallans = stats?.total || 0;

    if (!hasFilters) {
      return `Total: ${totalDbChallans.toLocaleString()} challans in database`;
    }

    const filterParts = [];
    if (search) filterParts.push(`"${search}"`);
    if (paid !== "") filterParts.push(paid === "true" ? "paid" : "unpaid");
    if (startDate) filterParts.push(`from: ${startDate}`);
    if (endDate) filterParts.push(`to: ${endDate}`);
    if (specificDate) filterParts.push(`date: ${specificDate}`);
    if (minAmount)
      filterParts.push(
        `min: ${isAdmin ? "N/A" : formatCurrency(parseInt(minAmount))}`
      );
    if (maxAmount)
      filterParts.push(
        `max: ${isAdmin ? "N/A" : formatCurrency(parseInt(maxAmount))}`
      );
    if (submitDate) filterParts.push(`submit: ${submitDate}`);

    const filterText =
      filterParts.length > 0 ? ` (${filterParts.join(", ")})` : "";
    return `Showing ${total.toLocaleString()} filtered challans${filterText}`;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (search) count++;
    if (paid !== "") count++;
    if (startDate) count++;
    if (endDate) count++;
    if (specificDate) count++;
    if (minAmount) count++;
    if (maxAmount) count++;
    if (submitDate) count++;
    return count;
  };

  // Use stats data instead of current page data
  const paidCount = stats?.paid || 0;
  const unpaidCount = stats?.unpaid || 0;
  const totalRevenue = stats?.totalRevenue || 0;
  const pendingRevenue = stats?.pendingRevenue || 0;

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  console.log("challans", challans);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Challans Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getFilterSummary()}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchChallans} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Note:</strong> Revenue information is not displayed for admin
          users as per system configuration.
        </Alert>
      )}

      {/* Summary Cards */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Paid Challans
                </Typography>
                <Typography variant="h5" color="success.main">
                  {isAdmin ? "N/A" : paidCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAdmin ? "Not available" : formatCurrency(totalRevenue)}
                </Typography>
              </Box>
              <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Unpaid Challans
                </Typography>
                <Typography variant="h5" color="error.main">
                  {isAdmin ? "N/A" : unpaidCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAdmin ? "Not available" : formatCurrency(pendingRevenue)}
                </Typography>
              </Box>
              <CancelIcon color="error" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h5" color="primary.main">
                  {isAdmin ? "N/A" : formatCurrency(totalRevenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAdmin ? "Not available" : "Collected"}
                </Typography>
              </Box>
              <MoneyIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography color="textSecondary" gutterBottom>
                  Pending Revenue
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {isAdmin ? "N/A" : formatCurrency(pendingRevenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAdmin ? "Not available" : "Outstanding"}
                </Typography>
              </Box>
              <ReceiptIcon color="warning" sx={{ fontSize: 40 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center">
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            {getActiveFilterCount() > 0 && (
              <Chip
                label={`${getActiveFilterCount()} active`}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          {getActiveFilterCount() > 0 && (
            <Typography variant="body2" color="primary">
              {total.toLocaleString()} results found
            </Typography>
          )}
        </Box>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Search"
            placeholder="Challan ID, Transaction ID"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={paid}
              label="Payment Status"
              onChange={(e) => setPaid(e.target.value as string)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Paid</MenuItem>
              <MenuItem value="false">Unpaid</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Filter by Date"
            type="date"
            value={specificDate}
            onChange={(e) => {
              setSpecificDate(e.target.value);
              setStartDate("");
              setEndDate("");
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setSpecificDate("");
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            disabled={!!specificDate}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setSpecificDate("");
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            disabled={!!specificDate}
          />

          <TextField
            label="Min Amount"
            type="number"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            placeholder="0"
            sx={{ minWidth: 120 }}
          />

          <TextField
            label="Max Amount"
            type="number"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            placeholder="10000"
            sx={{ minWidth: 120 }}
          />

          <TextField
            label="Submit Date"
            type="date"
            value={submitDate}
            onChange={(e) => setSubmitDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <IconButton onClick={clearFilters} title="Clear Filters">
            <CancelIcon />
          </IconButton>

          <Tooltip title="Download filtered challans as CSV (Choose Columns)">
            <IconButton
              onClick={handleOpenDownloadModal}
              disabled={loading}
              color="primary"
              sx={{ ml: 1 }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Challans Table */}
      <Paper sx={{ width: "100%", overflow: "hidden", position: "relative" }}>
        {loading && <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2 }} />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Roll & Mobile</TableCell>
                <TableCell>Courses</TableCell>
                <TableCell>Challan ID</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Transaction Details</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>
              {loading && challans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Box sx={{ py: 4 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : challans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No challans found
                  </TableCell>
                </TableRow>
              ) : (
                challans.map((challan) => (
                  <TableRow key={challan._id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar
                          sx={{
                            mr: 2,
                            bgcolor: "primary.main",
                            width: 40,
                            height: 40,
                          }}
                        >
                          {challan.userId?.fullName ? (
                            getInitials(challan.userId.fullName)
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {challan.userId?.fullName || "Unknown User"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {challan.userId?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        R: {challan.userId?.rollNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        M: {challan.userId?.mobile}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {challan.userId?.courses &&
                        challan.userId.courses.length > 0 ? (
                          challan.userId.courses.map((course, idx) => (
                            <Chip
                              key={idx}
                              label={course}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {challan.challanId}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="h6" color="primary">
                        {isAdmin ? "N/A" : formatCurrency(challan.amount)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        icon={
                          challan.paid ? <CheckCircleIcon /> : <CancelIcon />
                        }
                        label={challan.paid ? "Paid" : "Unpaid"}
                        color={challan.paid ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      {challan.paid ? (
                        <Box>
                          {challan.txnId && (
                            <Typography variant="body2">
                              Txn ID: {challan.txnId}
                            </Typography>
                          )}
                          {challan.branchCode && (
                            <Typography variant="body2" color="text.secondary">
                              Branch: {challan.branchCode}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(challan.createdAt)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {challan.paid ? formatDate(challan.txnDate) : "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip title="Generate Challan PDF">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleGeneratePDF(challan)}
                          disabled={
                            !challan.userId ||
                            challan.userId._id === "deleted" ||
                            challan.userId._id === "invalid"
                          }
                        >
                          <PdfIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* PDF Generation Modal */}
      <Dialog
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PdfIcon color="info" sx={{ mr: 1 }} />
            Generate Challan PDF
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Challan Generation:</strong> This will generate or open the
            challan PDF for the selected user in a new tab. If a challan already
            exists, it will be opened. If not, a new one will be created and
            opened.
          </Alert>
          {selectedChallanForPDF && selectedChallanForPDF.userId && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>User:</strong> {selectedChallanForPDF.userId.fullName}
              <br />
              <strong>Roll Number:</strong>{" "}
              {selectedChallanForPDF.userId.rollNumber}
              <br />
              <strong>Email:</strong> {selectedChallanForPDF.userId.email}
              <br />
              <strong>Current Challan ID:</strong>{" "}
              {selectedChallanForPDF.challanId}
            </Alert>
          )}
          <TextField
            label="Admin API Key (Required)"
            placeholder="Enter your admin API key..."
            value={adminApiKey}
            onChange={(e) => setAdminApiKey(e.target.value)}
            fullWidth
            type="password"
            required
            helperText="This API key is required to authenticate the PDF generation request"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfModalOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmGeneratePDF}
            variant="contained"
            color="info"
            disabled={!adminApiKey.trim() || pdfGenerating}
            startIcon={
              pdfGenerating ? <CircularProgress size={20} /> : <PdfIcon />
            }
          >
            {pdfGenerating
              ? "Processing Challan..."
              : "Generate & Open Challan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Column Selection Modal */}
      <Dialog
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ViewColumnIcon color="primary" />
            <Typography variant="h6">Select Columns to Export</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Choose the columns you want to include in the downloaded CSV file. Active filters will be respected.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Campaign Preset / Quick Selection */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2.5,
              backgroundColor: "rgba(25, 118, 210, 0.04)",
              border: "1px dashed",
              borderColor: "primary.main",
              borderRadius: 1.5,
            }}
          >
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1.5}
            >
              <Box>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                  Platform Mailer Template Preset
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Quickly select the required campaign columns: <code>username</code>, <code>duedate</code>, <code>challanId</code>, <code>paddedChallanId</code>, <code>psid</code>
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedColumns(["username", "duedate", "challanId", "paddedChallanId", "psid"]);
                }}
              >
                Use Campaign Preset
              </Button>
            </Box>
          </Paper>

          {/* Export Settings: Due Date & PSID Prefix */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mb: 2.5,
              p: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <TextField
              label="Challan Due Date (duedate column)"
              type="date"
              size="small"
              value={exportDueDate}
              onChange={(e) => setExportDueDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Value populated in the duedate column"
              fullWidth
            />
            <TextField
              label="PSID Prefix (psid column)"
              size="small"
              value={exportPsidPrefix}
              onChange={(e) => setExportPsidPrefix(e.target.value)}
              placeholder="e.g. 267200309"
              helperText="Prefixed before each Challan ID (Default: 267200309)"
              fullWidth
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Selected: {selectedColumns.length} of {AVAILABLE_EXPORT_COLUMNS.length} columns
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAllColumns}
                disabled={selectedColumns.length === AVAILABLE_EXPORT_COLUMNS.length}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={handleDeselectAllColumns}
                disabled={selectedColumns.length === 0}
              >
                Clear All
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 1.5,
            }}
          >
            {AVAILABLE_EXPORT_COLUMNS.map((column) => {
              const isChecked = selectedColumns.includes(column.key);
              return (
                <Paper
                  key={column.key}
                  variant="outlined"
                  sx={{
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 1,
                    cursor: "pointer",
                    borderColor: isChecked ? "primary.main" : "divider",
                    backgroundColor: isChecked ? "action.hover" : "background.paper",
                    transition: "all 0.15s ease-in-out",
                    "&:hover": {
                      backgroundColor: "action.selected",
                    },
                  }}
                  onClick={() => handleToggleColumn(column.key)}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={() => handleToggleColumn(column.key)}
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  <Typography variant="body2" sx={{ userSelect: "none" }}>
                    {column.label}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDownloadModalOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={downloadChallans}
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            disabled={selectedColumns.length === 0 || loading}
          >
            {loading ? "Exporting..." : `Download CSV (${selectedColumns.length} Cols)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Challans;
